import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import applicationService from '../services/applicationService';

const DOCUMENT_TYPES = [
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'PHOTO', label: 'Photo' },
  { value: 'CERTIFICATE', label: 'Certificate' },
  { value: 'TPC_CERTIFICATE', label: 'TPC Certificate' },
  { value: 'ESD_CERTIFICATE', label: 'ESD Certificate' },
  { value: 'CLEARANCE_CERTIFICATE', label: 'Clearance Certificate' },
  { value: 'MEDICAL_REPORT', label: 'Medical Report' },
  { value: 'VISA_DOCUMENT', label: 'Visa Document' },
  { value: 'OTHER', label: 'Other' }
];

const STAGE_REQUIRED_TYPES = {
  MEDICAL: ['MEDICAL_REPORT'],
  VISA_PROCESS: ['VISA_DOCUMENT']
};

const DocumentUpload = ({ applicationId, application, currentStage, onUploadSuccess }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedType, setSelectedType] = useState('PASSPORT');
  const [customDocumentName, setCustomDocumentName] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [stageFormFile, setStageFormFile] = useState(null);
  const [stageFormUploading, setStageFormUploading] = useState(false);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await applicationService.getApplicationDocuments(applicationId);
      if (response.success) {
        setDocuments(response.data);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        setSelectedFile(null);
        return;
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPG, PNG, and PDF files are allowed');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    if (stageSpecificForCandidate && !requiredTypesForStage.includes(selectedType)) {
      setError(`At this stage, only these documents are allowed: ${requiredTypeLabels}`);
      return;
    }

    if (selectedType === 'OTHER' && !customDocumentName.trim()) {
      setError('Please enter a name for the Other document');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const response = await applicationService.uploadDocument(
        applicationId,
        selectedFile,
        selectedType,
        selectedType === 'OTHER' ? customDocumentName.trim() : ''
      );

      if (response.success) {
        setSelectedFile(null);
        setSelectedType('PASSPORT');
        setCustomDocumentName('');
        // Reset file input
        const fileInput = document.getElementById('document-file-input');
        if (fileInput) fileInput.value = '';
        
        await fetchDocuments();
        if (onUploadSuccess) onUploadSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setActionLoading({ ...actionLoading, [documentId]: true });
    try {
      await applicationService.deleteDocument(applicationId, documentId);
      await fetchDocuments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete document');
    } finally {
      setActionLoading({ ...actionLoading, [documentId]: false });
    }
  };

  const handleVerify = async (documentId) => {
    setActionLoading({ ...actionLoading, [documentId]: true });
    try {
      await applicationService.verifyDocument(applicationId, documentId);
      await fetchDocuments();
      if (onUploadSuccess) onUploadSuccess();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to verify document');
    } finally {
      setActionLoading({ ...actionLoading, [documentId]: false });
    }
  };

  const handleStageFormUpload = async () => {
    if (!stageFormFile) {
      setError('Please select a form file to upload');
      return;
    }

    setStageFormUploading(true);
    setError('');

    try {
      if (currentStage === 'MEDICAL') {
        await applicationService.uploadMedicalForm(applicationId, stageFormFile);
      } else if (currentStage === 'VISA_PROCESS') {
        await applicationService.uploadVisaForm(applicationId, stageFormFile);
      }

      setStageFormFile(null);
      const stageFileInput = document.getElementById('stage-form-file-input');
      if (stageFileInput) stageFileInput.value = '';
      if (onUploadSuccess) onUploadSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload form');
    } finally {
      setStageFormUploading(false);
    }
  };

  const handleVisaApply = async () => {
    setStageFormUploading(true);
    setError('');

    try {
      await applicationService.applyForVisaProcess(applicationId);
      if (onUploadSuccess) onUploadSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark visa as applied');
    } finally {
      setStageFormUploading(false);
    }
  };

  const handleReject = async (documentId) => {
    const reason = prompt('Please enter rejection reason (minimum 10 characters):');
    if (!reason || reason.length < 10) {
      alert('Rejection reason must be at least 10 characters');
      return;
    }

    setActionLoading({ ...actionLoading, [documentId]: true });
    try {
      await applicationService.rejectDocument(applicationId, documentId, reason);
      await fetchDocuments();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to reject document');
    } finally {
      setActionLoading({ ...actionLoading, [documentId]: false });
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'VERIFIED':
        return 'bg-[#d4edda] text-[#155724]';
      case 'REJECTED':
        return 'bg-[#f8d7da] text-[#721c24]';
      case 'PENDING':
      default:
        return 'bg-[#fff3cd] text-[#856404]';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDocumentTypeLabel = (doc) => {
    if (doc?.documentType !== 'OTHER') {
      return doc?.documentType;
    }

    const fileName = String(doc?.fileName || '').trim();
    if (!fileName) {
      return 'OTHER';
    }

    // For OTHER docs, show custom name without extension as the headline label.
    const withoutExtension = fileName.replace(/\.[^.]+$/, '').trim();
    return withoutExtension || fileName;
  };

  const getDocumentViewUrl = (url, fileName = '') => {
    if (!url) return url;

    const lowerName = (fileName || '').toLowerCase();
    const lowerUrl = String(url).toLowerCase();
    const isPreviewableDoc =
      lowerName.endsWith('.pdf') ||
      lowerName.endsWith('.doc') ||
      lowerName.endsWith('.docx') ||
      lowerUrl.includes('.pdf') ||
      lowerUrl.includes('.doc') ||
      lowerUrl.includes('.docx');

    if (isPreviewableDoc) {
      return `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(url)}`;
    }

    return url;
  };

  const isCandidate = user?.role === 'CANDIDATE';
  const isHrOrAdmin = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role);
  const isMedicalStage = currentStage === 'MEDICAL';
  const isVisaStage = currentStage === 'VISA_PROCESS';
  const hasPassedVisaStage = ['PRE_DEPARTURE', 'CLEARANCE', 'TICKET', 'DEPARTURE', 'COMPLETED'].includes(currentStage);
  const canUpload = ['CANDIDATE', 'ADMIN', 'HR', 'CHAIRMAN'].includes(user?.role) && !(isCandidate && isVisaStage);
  const canVerify = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role);
  const canDelete = (doc) => {
    if (user?.role === 'ADMIN') return doc.verificationStatus !== 'VERIFIED';
    if (user?.role === 'CHAIRMAN') return doc.verificationStatus !== 'VERIFIED';
    if (user?.role === 'CANDIDATE') return doc.verificationStatus !== 'VERIFIED' && !isVisaStage;
    return false;
  };
  const shouldShowStageFormPanel = isMedicalStage || isVisaStage || (hasPassedVisaStage && application?.visa?.formFileUrl);
  const stageFormUrl = isMedicalStage ? application?.medical?.formFileUrl : application?.visa?.formFileUrl;
  const stageFormName = isMedicalStage
    ? (application?.medical?.formFileName || 'medical-form.pdf')
    : (application?.visa?.formFileName || 'visa-form.pdf');
  const stageLabel = isMedicalStage ? 'Medical' : 'Visa';
  const visaAppliedAt = application?.visa?.adminAppliedAt;
  const requiredTypesForStage = STAGE_REQUIRED_TYPES[currentStage] || [];
  const stageSpecificForCandidate = isCandidate && requiredTypesForStage.length > 0;
  const allowedUploadTypes = stageSpecificForCandidate
    ? DOCUMENT_TYPES.filter((type) => requiredTypesForStage.includes(type.value))
    : DOCUMENT_TYPES;
  const requiredTypeLabels = DOCUMENT_TYPES
    .filter((type) => requiredTypesForStage.includes(type.value))
    .map((type) => type.label)
    .join(', ');
  const hideRequiredDocumentsError = typeof error === 'string' && /Required documents missing/i.test(error);

  useEffect(() => {
    if (!stageSpecificForCandidate) return;
    if (!requiredTypesForStage.includes(selectedType)) {
      setSelectedType(requiredTypesForStage[0]);
    }
  }, [stageSpecificForCandidate, currentStage, selectedType, requiredTypesForStage]);

  return (
    <div className="bg-white rounded-lg p-6 shadow-[0_2px_4px_rgba(0,0,0,0.1)] mt-6">
      <h3 className="m-0 mb-5 text-[#2c3e50] text-xl">Documents</h3>

      {canUpload && (
        <form onSubmit={handleUpload} className="bg-[#f8f9fa] p-5 rounded-md mb-6">
          <div className="grid grid-cols-[auto_1fr_auto_2fr_1fr] gap-4 items-center md:grid-cols-1 md:gap-3 min-[960px]:[&>button]:justify-self-end">
            <label htmlFor="document-type" className="font-medium text-[#495057] text-[0.9rem] whitespace-nowrap">Document Type</label>
            <select
              id="document-type"
              value={selectedType}
              onChange={(e) => {
                setSelectedType(e.target.value);
                if (e.target.value !== 'OTHER') {
                  setCustomDocumentName('');
                }
              }}
              disabled={uploading || stageSpecificForCandidate}
              className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            >
              {allowedUploadTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>

            {selectedType === 'OTHER' && (
              <>
                <label htmlFor="custom-document-name" className="font-medium text-[#495057] text-[0.9rem] whitespace-nowrap md:mb-1">Document Name</label>
                <input
                  id="custom-document-name"
                  type="text"
                  value={customDocumentName}
                  onChange={(e) => setCustomDocumentName(e.target.value)}
                  placeholder="Enter custom document name"
                  disabled={uploading}
                  className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
                />
              </>
            )}

            <label htmlFor="document-file-input" className="font-medium text-[#495057] text-[0.9rem] whitespace-nowrap md:mb-1">Select File</label>
            <input
              id="document-file-input"
              type="file"
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.pdf"
              disabled={uploading}
              className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white"
            />

            <button
              type="submit"
              className="px-3 py-1.5 bg-accent-green text-white rounded border-none text-[13px] font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap max-w-fit hover:bg-[#5a6b4d] disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={uploading || !selectedFile}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          
          <small className="block mt-2 text-[#6c757d] text-[0.85rem]">
            Max 5MB. Allowed: JPG, PNG, PDF
          </small>

          {error && !hideRequiredDocumentsError && <div className="bg-[#f8d7da] text-[#721c24] p-3 rounded mt-3 border border-[#f5c6cb]">{error}</div>}
          
          {stageSpecificForCandidate && (
            <div className="bg-[#e7f3ff] text-[#004085] p-3 rounded mb-3 border border-[#b8daff] text-[0.9rem]">
              <strong>Required in this stage:</strong> {requiredTypeLabels}
            </div>
          )}
          <div className="bg-[#e7f3ff] text-[#004085] p-3 rounded mt-3 border border-[#b8daff] text-[0.9rem] leading-[1.5]">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{verticalAlign: 'middle', marginRight: '4px'}}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {isHrOrAdmin ? (
              <>
                <strong>Note:</strong> HR/Admin can upload multiple files for Other documents.
                For all other document types, only one file is allowed and you must delete the existing one before replacing it.
              </>
            ) : (
              <>
                <strong>Note:</strong> You can upload multiple documents of the same type.
                If a document is rejected, you can re-upload a new one.
                Only one verified document per type is allowed.
              </>
            )}
          </div>
        </form>
      )}

      {shouldShowStageFormPanel && (
        <div className="bg-[#f8f9fa] p-4 rounded-md mb-6 border border-[#dee2e6]">
          <h4 className="m-0 mb-3 text-[#2c3e50] text-base">{stageLabel} Form</h4>

          {isVisaStage && (
            <div className={`text-sm rounded p-2 mb-3 border ${visaAppliedAt ? 'text-[#155724] bg-[#d4edda] border-[#c3e6cb]' : 'text-[#6c757d] bg-[#f8f9fa] border-[#dee2e6]'}`}>
              {visaAppliedAt ? 'Application is applied for visa.' : (isCandidate ? 'Waiting for admin to mark visa as applied.' : 'Visa is not marked as applied yet.')}
            </div>
          )}

          {stageFormUrl ? (
            <div className="mb-3">
              <a
                href={getDocumentViewUrl(stageFormUrl, stageFormName)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 py-1.5 px-3 bg-white text-accent-green no-underline border border-accent-green rounded text-xs font-semibold transition-all duration-200 hover:bg-accent-green hover:text-white"
              >
                View {stageLabel} Form
              </a>
              <div className="text-[12px] text-[#6c757d] mt-1">{stageFormName}</div>
              {isCandidate && isVisaStage && (
                <div className="text-[12px] text-[#6c757d] mt-1">Uploaded by admin.</div>
              )}
            </div>
          ) : (
            <div className="text-[13px] text-[#6c757d] mb-3">{stageLabel} form has not been uploaded yet.</div>
          )}

          {isHrOrAdmin && isVisaStage && !visaAppliedAt && (
            <div className="mb-3">
              <button
                type="button"
                className="px-3 py-1.5 bg-[#007bff] text-white rounded border-none text-[13px] font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap max-w-fit hover:bg-[#0056b3] disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleVisaApply}
                disabled={stageFormUploading}
              >
                {stageFormUploading ? 'Processing...' : 'Mark Visa as Applied'}
              </button>
            </div>
          )}

          {isHrOrAdmin && (!isVisaStage || visaAppliedAt) && (
            <div>
              <label htmlFor="stage-form-file-input" className="font-medium text-[#495057] text-[0.9rem] whitespace-nowrap block mb-1">
                Upload {stageLabel} Form
              </label>
              <input
                id="stage-form-file-input"
                type="file"
                onChange={(e) => setStageFormFile(e.target.files?.[0] || null)}
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                disabled={stageFormUploading}
                className="p-2.5 border border-[#ced4da] rounded text-[0.95rem] bg-white w-full mb-2"
              />
              <button
                type="button"
                className="px-3 py-1.5 bg-[#007bff] text-white rounded border-none text-[13px] font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap max-w-fit hover:bg-[#0056b3] disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleStageFormUpload}
                disabled={stageFormUploading || !stageFormFile}
              >
                {stageFormUploading ? 'Uploading...' : `Upload ${stageLabel} Form`}
              </button>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-[#6c757d]">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-10 text-[#6c757d] bg-[#f8f9fa] rounded-md">No documents uploaded yet</div>
      ) : (
        <div className="flex flex-col gap-3">
          {documents.map(doc => (
            <div key={doc._id} className="flex justify-between items-center p-4 border border-[#dee2e6] rounded-md bg-[#f8f9fa] transition-all duration-200 hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:border-[#adb5bd] md:flex-col md:items-start md:gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-semibold text-[#2c3e50] text-base">{getDocumentTypeLabel(doc)}</span>
                  <span className={`px-3 py-1 rounded-xl text-[0.75rem] font-semibold uppercase ${getStatusBadgeClass(doc.verificationStatus)}`}>
                    {doc.verificationStatus}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[#495057] text-[0.9rem]">{doc.fileName}</span>
                  <span className="text-[#6c757d] text-[0.85rem]">Uploaded: {formatDate(doc.uploadedAt)}</span>
                  {doc.verifiedAt && (
                    <span className="text-[#6c757d] text-[0.85rem]">Verified: {formatDate(doc.verifiedAt)}</span>
                  )}
                  {doc.rejectionReason && (
                    <span className="text-[#dc3545] text-[0.85rem] italic">Reason: {doc.rejectionReason}</span>
                  )}
                </div>
              </div>

              <div className="flex gap-2 flex-wrap md:w-full md:justify-start">
                <a
                  href={getDocumentViewUrl(doc.fileUrl, doc.fileName)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 bg-[#007bff] text-white border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 no-underline inline-block whitespace-nowrap max-w-fit hover:bg-[#0056b3]"
                >
                  View
                </a>
                <a
                  href={doc.fileUrl}
                  download={doc.fileName}
                  className="px-3 py-1.5 bg-[#17a2b8] text-white border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 no-underline inline-block whitespace-nowrap hover:bg-[#138496]"
                  onClick={(e) => {
                    // For better download experience, we'll use fetch
                    e.preventDefault();
                    fetch(doc.fileUrl)
                      .then(response => response.blob())
                      .then(blob => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = doc.fileName;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      })
                      .catch(err => {
                        console.error('Download failed:', err);
                        // Fallback to direct link
                        window.open(doc.fileUrl, '_blank');
                      });
                  }}
                >
                  Download
                </a>

                {canVerify && doc.verificationStatus === 'PENDING' && (
                  <>
                    <button
                      onClick={() => handleVerify(doc._id)}
                      className="px-3 py-1.5 bg-[#28a745] text-white border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 inline-block whitespace-nowrap hover:bg-[#218838] disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={actionLoading[doc._id]}
                    >
                      Verify
                    </button>
                    <button
                      onClick={() => handleReject(doc._id)}
                      className="px-3 py-1.5 bg-[#ffc107] text-[#212529] border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 inline-block whitespace-nowrap hover:bg-[#e0a800] disabled:opacity-60 disabled:cursor-not-allowed"
                      disabled={actionLoading[doc._id]}
                    >
                      Reject
                    </button>
                  </>
                )}

                {canDelete(doc) && (
                  <button
                    onClick={() => handleDelete(doc._id)}
                    className="px-3 py-1.5 bg-[#dc3545] text-white border-none rounded text-[13px] font-semibold cursor-pointer transition-all duration-200 inline-block whitespace-nowrap hover:bg-[#c82333] disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={actionLoading[doc._id]}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
